import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { makeStyles } from '@material-ui/styles';
import PropTypes from 'prop-types';
import { Input, Typography, Button, message, Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { MUTATION_LANDLORD_ACCOUNT_LOGIN } from '../queries';
import { authActionTypes } from '~client/constants/actionTypes';
import generatePath from '~settings/routing';
import event from '~helpers/mixpanel';

const useStyles = makeStyles(
  {
    errText: {
      textAlign: 'left',
      color: '#F25202',
    },
    inputErr: {
      borderColor: '#F25202 !important',

      '&:hover': {
        borderColor: '#F25202 !important',
        boxShadow: 'none !important',
      },
    },
    emailText: {
      marginTop: '12px !important',
      marginBottom: '40px !important',
    },
    passwordWrapper: {
      position: 'relative',
    },
    forgetPassword: {
      position: 'absolute',
      top: 10,
      right: 16,
      cursor: 'pointer',
      zIndex: 1,
      fontSize: '14px',
      lineHeight: '22px',
      color: '#38B2A6',
    },
    openEmailText: {
      color: '#f25203',
      textDecoration: 'underline',
    },
    checkbox: {
      display: 'flex',
      fontSize: '12px !important',
      lineHeight: '22px !important',
      color: '#595959 !important',
      marginTop: '16px !important',

      '& a': {
        textDecoration: 'underline',
      },

      '& > *:first-child': {
        width: 16,
        height: 16,
        marginTop: 4,
      },
    },
  },
  { name: 'LoginForSetEmailPassword' },
);

export const LoginForSetEmailPassword = ({ loginEmail, history, showWarning }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const classes = useStyles();

  const [password, setPassword] = useState();
  const [emailErrTip, setEmailErrMessage] = useState();
  const [isDisabled, setIsDisabled] = useState(true);
  const [isDisableUser, setDisaledUser] = useState(false);

  const handelChangePassword = e => {
    const value = e?.target?.value;
    setPassword(value);
    setDisaledUser(false);
    setEmailErrMessage();
  };

  const handelValidateTriggerPassword = value => {
    if (!value) {
      setEmailErrMessage(t('this_part_cannot_be_empty'));
      return;
    }

    if (value?.length < 8) {
      setEmailErrMessage(t('landlord.auth.login.format_error.password'));
      return;
    }
  };

  const [mutationLandlordAccountLogin, { loading: landlordAccountLoginLoading }] = useMutation(
    MUTATION_LANDLORD_ACCOUNT_LOGIN,
    {
      variables: { input: { email: loginEmail, password } },
      onCompleted: successData => {
        const authToken = successData?.landlordAccountLogin?.authToken;
        event('Successfully Logged In');
        dispatch({ type: authActionTypes.LOGGED_IN, token: authToken });
        //login succ
      },
      onError: err => {
        const errMessage = err?.message;
        if (errMessage && errMessage.includes('INVALID_CREDENTIALS')) {
          setEmailErrMessage(t('sorry_the_password_and_email_does_not_match'));
          return;
        }
        // landlord 被cms disabled之后的报错
        if (errMessage && errMessage.includes('USER_NOT_ENABLED_ERROR')) {
          setDisaledUser(true);
          setEmailErrMessage(`${t('sorry_user_does_disabled')}`);
          return;
        }
        if (err?.message) {
          message.error(err?.message);
        } else {
          message.error(t('sorry_an_error_occurred_please_try_again'));
        }
      },
    },
  );

  const handleClickContinue = async () => {
    if (loginEmail && !emailErrTip) {
      event('Clicked Login Email Password Page', {
        'Clicked Position': 'Continue Btn',
      });

      await mutationLandlordAccountLogin({ email: loginEmail, password });
    }
  };

  const handleClickForgetPassword = () => {
    event('Clicked Login Email Password Page', {
      'Clicked Position': 'Forgot Password',
    });

    history.push(generatePath('resetPassword', { email: loginEmail }));
  };

  const handleWarningCheckboxChange = ev => {
    setIsDisabled(!ev.target.checked);
  };

  useEffect(() => {
    event('Viewed Login Email Password Page');
  }, []);

  return (
    <>
      <Typography className={classes.emailText}>
        {t('enter_password_for_email', { email: loginEmail })}
      </Typography>
      <div className={classes.passwordWrapper}>
        <span
          className={classes.forgetPassword}
          role="presentation"
          onClick={handleClickForgetPassword}
        >
          {t('landlord.log_in.main.link.forget_password')}
        </span>
        <Input
          size="large"
          type="password"
          placeholder={t('password')}
          onChange={handelChangePassword}
          onBlur={e => {
            handelValidateTriggerPassword(e?.target?.value);
          }}
          onFocus={() =>
            event('Clicked Login Email Password Page', {
              'Clicked Position': 'Password Box',
            })
          }
          style={{ paddingRight: 130 }}
          className={emailErrTip ? classes.inputErr : ''}
        />
        <Typography className={classes.errText} style={{ marginTop: 4 }}>
          {emailErrTip}
          {isDisableUser && (
            <a target="_blank" href="mailto:contact@student.com" rel="noreferrer">
              <span className={classes.openEmailText}>{t('login_error_email_us')}</span>
            </a>
          )}
        </Typography>
      </div>
      {showWarning && (
        <Checkbox
          checked={!isDisabled}
          onChange={handleWarningCheckboxChange}
          className={classes.checkbox}
        >
          {t('unable_to_serve_students_searching')}
        </Checkbox>
      )}
      <Button
        type="primary"
        size="large"
        loading={landlordAccountLoginLoading}
        onClick={handleClickContinue}
        disabled={showWarning && isDisabled}
      >
        {t('continue')}
      </Button>
    </>
  );
};

LoginForSetEmailPassword.propTypes = {
  loginEmail: PropTypes.string.isRequired,
  history: PropTypes.object,
  handleNextContent: PropTypes.func.isRequired,
};

export default LoginForSetEmailPassword;
